import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { CLIENT_TYPE_LABELS, SITE_TYPE_LABELS, apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { ClientBillingTab } from '@/components/clients/ClientBillingTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClientForm, toClientPayload, type ClientFormData } from '@/components/clients/ClientForm';
import { SiteDialog } from '@/components/clients/SiteDialog';

import type { Client, Site } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/clients/$id')({ component: ClientDetailPage });

function ClientDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const canWrite = useHasPermission('missions.write');

  const { data: client, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: async () => (await api.get<Client>(`/clients/${id}`)).data,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['client', id] });
    void qc.invalidateQueries({ queryKey: ['clients'] });
  };

  const update = useMutation({
    mutationFn: (data: ClientFormData) => api.patch(`/clients/${id}`, toClientPayload(data, null)),
    onSuccess: () => {
      invalidate();
      toast.success('Client modifié');
      setEditing(false);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la modification')),
  });

  const toggleActive = useMutation({
    mutationFn: (active: boolean) => api.patch(`/clients/${id}`, { active }),
    onSuccess: (_, active) => {
      invalidate();
      toast.success(active ? 'Client réactivé' : 'Client désactivé');
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors du changement de statut')),
  });

  if (isLoading) return <div className="p-6"><Skeleton className="h-48 w-full" /></div>;
  if (!client) return <div className="p-6 text-muted-foreground">Client introuvable</div>;

  const onToggleActive = () => {
    if (client.active && !window.confirm(`Désactiver le client « ${client.name} » ? Il n'apparaîtra plus dans les nouvelles missions.`)) return;
    toggleActive.mutate(!client.active);
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/clients"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link>
        </Button>
        <h1 className="text-2xl font-bold">{client.name}</h1>
        <Badge variant={client.active ? 'default' : 'secondary'}>{client.active ? 'ACTIF' : 'DÉSACTIVÉ'}</Badge>
        {canWrite && (
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={onToggleActive} disabled={toggleActive.isPending}>
              {client.active ? 'Désactiver' : 'Réactiver'}
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Informations</TabsTrigger>
          <TabsTrigger value="sites">Sites ({client.sites?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="billing">Tarif &amp; factures</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Fiche client</CardTitle>
              {!editing && canWrite && (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil className="h-4 w-4 mr-1" /> Modifier
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {editing ? (
                <ClientForm
                  initial={client}
                  submitLabel="Enregistrer"
                  pending={update.isPending}
                  onSubmit={(d) => update.mutate(d)}
                  onCancel={() => setEditing(false)}
                />
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Field label="Nom" value={client.name} />
                  <Field label="Secteur" value={CLIENT_TYPE_LABELS[client.type] ?? client.type} />
                  <Field label="Contact" value={client.contactName} />
                  <Field label="Téléphone" value={client.phone} />
                  <Field label="Email" value={client.email} />
                  <Field label="N° fiscal (NIF)" value={client.vatNumber} />
                  <Field label="Adresse" value={client.address} />
                  <Field label="Client depuis" value={new Date(client.createdAt).toLocaleDateString('fr-FR')} />
                  {client.notes && (
                    <div className="col-span-2">
                      <Field label="Notes" value={client.notes} />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sites">
          <SitesTab clientId={client.id} sites={client.sites ?? []} />
        </TabsContent>

        <TabsContent value="billing">
          <ClientBillingTab client={client} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SitesTab({ clientId, sites }: { clientId: string; sites: Site[] }) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | undefined>();
  const canWrite = useHasPermission('missions.write');
  const canDelete = useHasPermission('missions.delete');

  const remove = useMutation({
    mutationFn: (siteId: string) => api.delete(`/sites/${siteId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['client', clientId] });
      void qc.invalidateQueries({ queryKey: ['sites'] });
      toast.success('Site supprimé');
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la suppression')),
  });

  const openDialog = (site?: Site) => {
    setEditingSite(site);
    setDialogOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Sites de chargement / livraison</CardTitle>
        {canWrite && (
          <Button size="sm" onClick={() => openDialog()}>
            <Plus className="h-4 w-4 mr-1" /> Ajouter un site
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Ville / Adresse</TableHead>
              <TableHead>Coordonnées GPS</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sites.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-semibold">{s.name}</TableCell>
                <TableCell>{SITE_TYPE_LABELS[s.type] ?? s.type}</TableCell>
                <TableCell>{[s.city, s.address].filter(Boolean).join(' — ') || '—'}</TableCell>
                <TableCell className="font-mono text-xs">
                  {s.lat != null && s.lon != null ? `${s.lat.toFixed(5)}, ${s.lon.toFixed(5)}` : (
                    <span className="text-warning">Non renseignées</span>
                  )}
                </TableCell>
                <TableCell>{[s.contactName, s.contactPhone].filter(Boolean).join(' · ') || '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {canWrite && (
                      <Button variant="ghost" size="sm" aria-label="Modifier le site" onClick={() => openDialog(s)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Supprimer le site"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Supprimer le site « ${s.name} » ?`)) remove.mutate(s.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-danger" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {sites.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Aucun site pour ce client
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
      <SiteDialog clientId={clientId} site={editingSite} open={dialogOpen} onOpenChange={setDialogOpen} />
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs uppercase tracking-wide">{label}</p>
      <p className="font-medium mt-0.5 whitespace-pre-wrap">{value || '—'}</p>
    </div>
  );
}
